<template>
  <div>
    <v-row
      class="mb-2">
      <v-col class="col-12 col-sm-9">
        <v-row
          v-if="!isLti && isFlipEdPlaylist"
          id="assignRow">
          <v-col class="pt-0 pl-0">
            <v-text-field
              class="ml-3"
              v-model="playlistURL"
              label="Share this link with learners"
              readonly
              @click="copyText('url')"
              @click:append="copyText('url')"
              flat
              append-icon="mdi-content-copy"
              hide-details>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row class="mt-0 pt-0">
          <v-col class="mt-0 pl-0">
            <v-color-picker
              :disabled="isUpdating"
              aria-label="Color picker for playlist. Tab for hex number text input."
              v-model="playlistColor"
              hide-canvas
              hide-mode-switch
              mode="hexa"
              width="100%"
              flat
              @input="debounceUpdateColor"></v-color-picker>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row
      class="mt-2 mb-2">
      <v-col
        class="col-12 col-sm-9 pl-0">
        <v-expansion-panels
          class="px-3 rounded-lg">
          <v-expansion-panel
            id="playlistSettingsPanel">
            <v-expansion-panel-header>
              Settings
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-text-field
                v-if="hasExposePlaylistId"
                class="mb-4"
                v-model="playlistId"
                label="Playlist ID"
                readonly
                @click="copyText('playlist')"
                @click:append="copyText('playlist')"
                flat
                append-icon="mdi-content-copy"
                hide-details>
              </v-text-field>
              <v-switch
                id="lockProgressionToggle"
                light
                dense
                class="py-0 mt-2 mb-2"
                :color="playlistColor"
                v-model="lockProgression"
                label="Lock progression"
                :disabled="isUpdating || restrictPlaylistAccess"
                hide-details
                @change="updatePlaylistSettings"
              ></v-switch>
              <v-layout
              class="justify-start mt-4"
              v-if="showPaywallButton">
                <v-tooltip
                top
                :disabled="playlist.config.paywall">
                <template v-slot:activator="{ on }">
                  <v-btn
                    id="requestPaywallButton"
                    small
                    v-on="on"
                    :disabled="playlist.config.paywall || user.teacherContainerRole !== 1"
                    class="mt-3"
                    @click="updatePlaylistPaywall"
                    aria-label="Request Paywall. Clicking this button will ask PlayPosit to add a pay wall to your playlist. Please check your inbox for an email within the next 2 business days.">
                    {{ paywallBtnText }}
                  </v-btn>
                </template>
                <span>Clicking this button will ask PlayPosit to add a pay wall to your playlist. Please check your inbox for an email within the next 2 business days.</span>
              </v-tooltip>
              </v-layout>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel
            id="playlistVisibilityPanel">
            <v-expansion-panel-header>
              Visibility
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-radio-group
                id="playlistVisibilityRadioGroup"
                v-model="listed"
                @change="updatePlaylistListed"
                class="py-0 my-0"
                :disabled="isUpdating || restrictPlaylistAccess">
                <v-radio
                  label="Playlists are public. (visible in Premade Bulbs & Media Library)"
                  class="pl-2"
                  :value="1"
                  :color="playlistColor"></v-radio>
                <v-radio
                  label="Playlists are visible to members of your district/institution."
                  class="pl-2"
                  :value="0"
                  :color="playlistColor"></v-radio>
                <v-radio
                  label="Playlists are visible to members of your school/site."
                  class="pl-2"
                  :value="2"
                  :color="playlistColor"></v-radio>
                <v-radio
                  label="Playlists are private. (not visible in Premade Bulbs & Media Library)"
                  class="pl-2"
                  :value="3"
                  :color="playlistColor"></v-radio>
              </v-radio-group>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel
              id="playlistVisibilityPanel">
              <v-expansion-panel-header>
                Tags
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <PlaylistTags
                  :playlist="playlist"
                  :activeColor="playlistColor"></PlaylistTags>
              </v-expansion-panel-content>
            </v-expansion-panel>
          <v-expansion-panel
            id="playlistCertificateSettingsPanel">
            <v-expansion-panel-header>
              Certificate Settings
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-switch
                id="playlistCertificateToggle"
                light
                dense
                class="mt-0 mb-3 pt-0 pb-2"
                :color="playlistColor"
                :disabled="isUpdating || restrictPlaylistAccess"
                v-model="playlistCertificateAccess"
                label="Enable playlist certificate"
                hide-details
                @change="updatePlaylistSettings">
              </v-switch>
              <v-switch
                id="playlistCertificateLMBToggle"
                v-if="playlistCertificateAccess && playlistHasLMBs"
                light
                dense
                class="mt-0 mb-3 pt-0 pb-2"
                :color="playlistColor"
                :disabled="isUpdating || restrictPlaylistAccess"
                v-model="playlistCertificateLMBAccess"
                label="Disable certificate access until LMBs graded"
                hide-details
                @change="updatePlaylistSettings">
              </v-switch>
              <PlaylistCertificateSettings v-if="playlistCertificateAccess"></PlaylistCertificateSettings>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </div>
</template>

<script>
/* global navigator */
import {
  mapState,
  mapGetters,
  mapActions,
  mapMutations,
} from 'vuex';
import {
  inDevEnvironment,
} from '@utilities';
import { mapFields } from 'vuex-map-fields';
import _ from 'lodash';
import Hashids from 'hashids';
import PlaylistCertificateSettings from './PlaylistCertificateSettings.vue';
import PlaylistTags from './PlaylistTags.vue';

const hashids = new Hashids('dachshund', 6, 'cdefhjkmnprtvwxy2345689');

export default {
  name: 'PlaylistSettings',
  components: {
    PlaylistCertificateSettings,
    PlaylistTags,
  },
  data: () => ({
    anonymous: false,
    baseURL: null,
    listed: null,
    lockProgression: false,
    paywall: false,
    playlistCertificateAccess: false,
    playlistCertificateLMBAccess: false,
    playlistColor: '#2196F3',
  }),
  computed: {
    ...mapState([
      'courseHash',
      'isLti',
      'isUpdating',
      'playlistHasLMBs',
      'sectionId',
      'user',
    ]),
    ...mapGetters([
      'restrictPlaylistAccess',
    ]),
    ...mapFields([
      'playlist',
    ]),
    hasExposePlaylistId() {
      return _.get(this, 'playlist.school.permissionKeys', []).includes('hasExposePlaylistId');
    },
    isFlipEdPlaylist() {
      return (
        _.get(this.playlist, 'config.sku')
        && _.get(this.playlist, 'sections', []).length === 1
      );
    },
    paywallBtnText() {
      if (_.get(this.playlist, 'config.sku', null)) return 'Paywall Added';
      if (_.get(this, 'playlist.config.paywall')) return 'Paywall requested';
      return 'Request Paywall';
    },
    playlistId() {
      return hashids.decode(this.courseHash);
    },
    playlistURL() {
      return `${this.baseURL}playlist/${hashids.encode([this.playlist.id, this.playlist.sections[0].id])}`;
    },
    showPaywallButton() {
      return _.get(this, 'user.school.config.permissions', []).includes('hasPlaylistPaywall');
    },
  },
  methods: {
    ...mapMutations([
      'toggleSnackbar',
    ]),
    ...mapActions([
      'requestPaywall',
      'updatePlaylist',
    ]),
    copyText(type) {
      this.toggleSnackbar({
        text: type === 'url' ? 'Course URL Copied!' : 'Playlist ID copied!',
        color: 'success',
        timeout: 2500,
      });
      navigator.clipboard.writeText(type === 'url' ? this.playlistURL : this.playlistId);
    },
    // eslint-disable-next-line
    debounceUpdateColor: _.debounce(function () {
      const [sliderThumb] = document.getElementsByClassName('v-slider__thumb-container');
      if (sliderThumb) {
        sliderThumb.setAttribute('aria-valuetext', `Playlist hex color: ${this.playlistColor}`);
        sliderThumb.removeAttribute('aria-valuenow');
      }
      this.updatePlaylist({
        defaultColor: this.playlistColor,
      });
    }, 800),
    updatePlaylistListed() {
      this.updatePlaylist({
        listed: this.listed,
      });
    },
    updatePlaylistPaywall() {
      this.requestPaywall({
        config: {
          open: false,
          lockProgression: this.lockProgression,
          anonymous: this.anonymous,
          paywall: true,
          certificateAccess: this.playlistCertificateAccess,
          certificateLMBAccess: this.playlistCertificateLMBAccess,
        },
      });
    },
    updatePlaylistSettings() {
      this.updatePlaylist({
        config: {
          certificateAccess: this.playlistCertificateAccess,
          certificateLMBAccess: this.playlistCertificateLMBAccess,
          lockProgression: this.lockProgression,
          nsf: this.playlist.config.nsf,
          sku: _.get(this.playlist, 'config.sku', null),
          paywall: this.paywall,
        },
      });
    },
  },
  watch: {
    // eslint-disable-next-line
    'playlist.config.paywall'() {
      this.paywall = this.playlist.config.paywall;
    },
  },
  beforeMount() {
    this.anonymous = _.get(this, 'playlist.config.anonymous', false);
    this.lockProgression = _.get(this, 'playlist.config.lockProgression', false);
    this.paywall = _.get(this, 'playlist.config.paywall', false);
    this.playlistCertificateAccess = _.get(this, 'playlist.config.certificateAccess', false);
  },
  mounted() {
    /*
      The default_color is set to a standard hex color (#2196F3) by default as it is the only way to remove
      the transparency picker from the Vuetify color picker.  Then on mounted we update it to the stored
      value from the DB
    */
    this.playlistColor = _.get(this, 'playlist.default_color') ? this.playlist.default_color : '#2196F3';
    this.playlistCertificateLMBAccess = _.get(this, 'playlist.config.certificateLMBAccess', false);
    this.listed = !_.isNil(_.get(this, 'playlist.listed')) ? this.playlist.listed : 3;
    this.baseURL = inDevEnvironment() ? 'https://wwwmatt.playpauseit.com/' : 'https://www.playposit.com/';
    const [sliderThumb] = document.getElementsByClassName('v-slider__thumb-container');
    if (sliderThumb) {
      sliderThumb.setAttribute('aria-valuetext', `Playlist hex color: ${this.playlistColor}`);
      sliderThumb.removeAttribute('aria-valuenow');
    }
  },
};
</script>

<style lang="scss">
  .v-color-picker__controls {
    padding-bottom: 0 !important;
  }
</style>
